/**
 * Registration Complete Page - Shows completion options after player registration
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import LoadingState from '../components/LoadingState';
import { useAuth } from '../contexts/AuthContext';
import { PartyPopper, Trophy, Users, CheckCircle, Activity, Mail } from 'lucide-react';
import styles from './RegistrationComplete.module.css';

const RegistrationComplete = () => {
  const { user, isAuthenticated, loading: authLoading, checkAuthStatus } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOfferRegistration = searchParams.get('offer') === 'true';
  const [countdown, setCountdown] = useState(3);
  const [autoRedirect, setAutoRedirect] = useState(!isOfferRegistration); // Don't auto-redirect for offer registrations

  // Redirect if not authenticated (but only after auth check is complete)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
      return;
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    // Refresh user data to get updated status
    if (!authLoading && isAuthenticated) {
      checkAuthStatus();
    }
  }, [isAuthenticated, authLoading, checkAuthStatus]);

  // Auto-redirect to dashboard after 5 seconds
  useEffect(() => {
    if (autoRedirect && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (autoRedirect && countdown === 0) {
      navigate('/dashboard');
    }
  }, [countdown, autoRedirect, navigate]);

  const handleViewPrograms = () => {
    setAutoRedirect(false);
    navigate('/programs');
  };

  const handleAddAnotherPlayer = () => {
    setAutoRedirect(false);
    navigate('/profile');
  };

  const handleGoToDashboard = () => {
    setAutoRedirect(false);
    navigate('/dashboard');
  };

  const handleStayOnPage = () => {
    setAutoRedirect(false);
  };

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <PageLayout maxWidth="1400px">
        <LoadingState />
      </PageLayout>
    );
  }

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <PageLayout maxWidth="1400px">
      <PageHeader
        title="🎉 REGISTRATION COMPLETE!"
        subtitle="Welcome to the soccer training family"
        subtitleStyle={{
          color: 'var(--primary-green)',
          fontSize: '1.3rem',
          fontWeight: '600',
          textTransform: 'none',
          letterSpacing: 'normal',
          lineHeight: '1.6',
          maxWidth: '600px',
          margin: '0 auto'
        }}
      />

      {/* Success Message */}
      <Section>
        <Card level={1} className={styles.successCard} style={{ background: 'var(--primary-green)', color: 'var(--primary-black)' }}>
        <div className={styles.successCardContent}>
          <div className={styles.successIcon}><Activity size={48} /></div>
          <h2 className={styles.successHeading}>
            Congratulations!
          </h2>
          <p className={styles.successMessage}>
            Your registration is now complete{user?.email ? `, ${user.email.split('@')[0]}` : ''}. Your first player has been successfully registered
            and you now have full access to all our soccer training programs.
          </p>
        </div>
      </Card>
      </Section>

      {/* Offer-specific message */}
      {isOfferRegistration && (
        <Section>
          <Card level={1} style={{ background: 'rgba(59, 130, 246, 0.1)', border: '2px solid #3b82f6' }}>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <Mail size={48} style={{ color: '#3b82f6', margin: '0 auto 1rem' }} />
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#3b82f6',
                marginBottom: '1rem'
              }}>
                Check Your Email
              </h2>
              <p style={{
                fontSize: '1.1rem',
                color: 'var(--color-text-primary)',
                lineHeight: 1.6,
                maxWidth: '600px',
                margin: '0 auto 1.5rem'
              }}>
                We've sent you an email with instructions to accept your team offer.
                Please check your inbox and click the "Accept & Join Team" button to complete your enrollment.
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/inbox')}
              >
                GO TO INBOX
              </Button>
            </div>
          </Card>
        </Section>
      )}

      {/* Auto-redirect notification */}
      {autoRedirect && (
        <Section>
          <Card level={1} className={styles.redirectCard}>
          <div className={styles.redirectContainer}>
            <p className={styles.redirectMessage}>
              Redirecting to your dashboard in {countdown} seconds...
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleStayOnPage}
            >
              Stay on this page
            </Button>
          </div>
        </Card>
        </Section>
      )}

      {/* Next Steps */}
      <Section>
        <div className={autoRedirect ? styles.nextStepsSection : styles.nextStepsSectionAutoRedirect}>
        <h2 className={styles.nextStepsHeading}>
          What would you like to do next?
        </h2>

        <div className={styles.optionsGrid}>
          {/* View Programs */}
          <Card level={2} className={styles.optionCard}>
            <div className={styles.optionCardContent}>
              <div className={styles.optionIcon}>
                <Trophy size={32} />
              </div>
              <h3 className={styles.optionHeading}>
                Explore Programs
              </h3>
              <p className={styles.optionDescription}>
                Browse our age-appropriate soccer training programs and find the perfect fit for your player.
              </p>
              <Button
                variant="primary"
                fullWidth
                onClick={handleViewPrograms}
              >
                View Programs
              </Button>
            </div>
          </Card>

          {/* Add Another Player */}
          <Card level={2} className={styles.optionCard}>
            <div className={styles.optionCardContent}>
              <div className={styles.optionIcon}>
                <Users size={32} />
              </div>
              <h3 className={styles.optionHeading}>
                Add Another Player
              </h3>
              <p className={styles.optionDescription}>
                Register additional players from your family. You can add more players anytime from your profile.
              </p>
              <Button
                variant="outline"
                fullWidth
                onClick={handleAddAnotherPlayer}
              >
                Add Player
              </Button>
            </div>
          </Card>
        </div>
      </div>
      </Section>

      {/* Account Summary */}
      <Section>
        <Card level={1} className={styles.summaryCard}>
        <div className={styles.summaryCardContent}>
          <h3 className={styles.summaryHeading}>
            Your Account Summary
          </h3>

          <div className={styles.summaryGrid}>
            <div className={styles.summaryItem}>
              <div className={styles.summaryItemIcon}>
                <CheckCircle size={24} />
              </div>
              <h4 className={styles.summaryItemHeading}>
                Registration Status
              </h4>
              <p className={styles.summaryItemText}>
                Complete
              </p>
            </div>

            <div className={styles.summaryItem}>
              <div className={styles.summaryItemIcon}>
                <Users size={24} />
              </div>
              <h4 className={styles.summaryItemHeading}>
                Parents Registered
              </h4>
              <p className={styles.summaryItemText}>
                Primary parent info saved
              </p>
            </div>

            <div className={styles.summaryItem}>
              <div className={styles.summaryItemIcon}>
                <Activity size={24} />
              </div>
              <h4 className={styles.summaryItemHeading}>
                Players Registered
              </h4>
              <p className={styles.summaryItemText}>
                1 player ready to play
              </p>
            </div>
          </div>
        </div>
      </Card>
      </Section>

      {/* Quick Actions */}
      <Section>
        <div className={styles.quickActionsSection}>
        <p className={styles.quickActionsMessage}>
          You can always access your dashboard to manage your account and players.
        </p>
        <Button
          variant="secondary"
          onClick={handleGoToDashboard}
        >
          Go to Dashboard
        </Button>
      </div>
      </Section>
    </PageLayout>
  );
};

export default RegistrationComplete;
